/**
 * AuthCallbackPage
 *
 * Handles the redirect from Cognito Managed Login, Identity Center SAML,
 * or Google OAuth callback flow. Receives a short-lived auth code from
 * the backend, exchanges it for tokens via POST, and redirects.
 *
 * Route: /auth/callback?code=...&redirect=/dashboard
 *
 * SCRUM-354, SCRUM-2100
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { exchangeAuthCode } from '../../services/authService';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokenFromCallback } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const redirectTo = searchParams.get('redirect') || '/';
    const oauthError = searchParams.get('error');

    if (oauthError) {
      setError(oauthError);
      return;
    }

    if (!code) {
      setError('No authorization code received.');
      return;
    }

    const performExchange = async () => {
      try {
        const data = await exchangeAuthCode(code);
        if (setTokenFromCallback) {
          await setTokenFromCallback(data.accessToken);
        }
        navigate(redirectTo, { replace: true });
      } catch (err) {
        setError('Failed to complete sign in. The link may have expired — please try again.');
      }
    };

    performExchange();
  }, [searchParams, navigate, setTokenFromCallback]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium"
          >
            Return to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
